import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { EASE, Reveal, SectionTag, Counter } from "./motion";

const STAGES = [
  {
    name: "ATRAIR",
    tag: "Marketing & Anúncios",
    desc: "Campanhas de captação de alta performance colocam a sua clínica na frente do paciente certo, na hora certa.",
    bullets: ["Meta Ads e Google Ads especializados em saúde", "Landing pages de alta conversão", "Posicionamento premium da marca"],
    metrics: [{ label: "Leads gerados", value: 759, suffix: "" }, { label: "Custo por lead", value: 5.7, prefix: "R$ ", decimals: 1 }],
  },
  {
    name: "ATENDER",
    tag: "Qualificação & Relacionamento",
    desc: "Cada lead recebe resposta em minutos por uma equipe treinada para qualificar e conduzir a conversa.",
    bullets: ["Atendimento em menos de 5 minutos", "Scripts de recepção e CRC", "Qualificação de oportunidades reais"],
    metrics: [{ label: "Agendamentos", value: 235, suffix: "" }, { label: "Taxa de contato", value: 92, suffix: "%" }],
  },
  {
    name: "CONVERTER",
    tag: "Agendamento, Comparecimento & Vendas",
    desc: "Confirmação ativa, redução de no-show e condução comercial transformam avaliações em tratamentos fechados.",
    bullets: ["Régua de confirmação anti-no-show", "Condução da avaliação para o fechamento", "Acompanhamento em tempo real"],
    metrics: [{ label: "Avaliações realizadas", value: 145, suffix: "" }, { label: "Fechamentos", value: 85, suffix: "" }],
  },
  {
    name: "RETER",
    tag: "Follow-up & Relacionamento",
    desc: "Nenhuma oportunidade é abandonada: follow-up estruturado recupera interessados e fideliza pacientes.",
    bullets: ["Automações de follow-up no WhatsApp", "Reativação de base de leads", "Programas de recorrência"],
    metrics: [{ label: "Leads reativados", value: 31, suffix: "%" }, { label: "Retorno de pacientes", value: 2.4, suffix: "x", decimals: 1 }],
  },
  {
    name: "ESCALAR",
    tag: "Análise de Dados & Crescimento",
    desc: "Com indicadores claros em cada etapa, a clínica sabe exatamente onde investir para crescer com previsibilidade.",
    bullets: ["Dashboard de indicadores comerciais", "ROI por campanha e por canal", "Plano de expansão orientado a dados"],
    metrics: [{ label: "Em vendas", value: 435, prefix: "R$ ", suffix: " mil" }, { label: "Decisões por dados", value: 100, suffix: "%" }],
  },
];

const W = [600, 500, 400, 310, 240];
const B = [500, 400, 310, 240, 180];
const PARTICLES = [0, -52, 38, -22, 58, -38, 14, -70, 30];

export default function Funnel() {
  const [active, setActive] = useState(0);
  const manualTs = useRef(0);
  const sectionRef = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.65", "end 0.6"],
  });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    if (Date.now() - manualTs.current < 2500) return;
    setActive(Math.min(4, Math.max(0, Math.floor(v * 5.4 - 0.4))));
  });

  const select = (i) => {
    manualTs.current = Date.now();
    setActive(i);
  };

  const stage = STAGES[active];

  return (
    <section id="metodo" ref={sectionRef} data-testid="funnel-section" className="bg-graphite py-28 lg:py-36 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] lg:w-[700px] lg:h-[700px] rounded-full bg-gold/5 blur-[70px] lg:blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-3xl">
          <Reveal>
            <SectionTag>Método Pulse</SectionTag>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-bone leading-[1.12]" data-testid="funnel-title">
              Um funil completo. <span className="text-gold">Do anúncio à escala.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base sm:text-lg text-bone/60 leading-relaxed">
              Role a página ou toque em cada etapa para ver como o Método Pulse transforma
              cliques em crescimento previsível.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="order-2 lg:order-1">
            <svg viewBox="0 0 640 600" className="w-full max-w-[560px] mx-auto" data-testid="funnel-svg" role="img" aria-label="Funil do Método Pulse com 5 etapas">
              <defs>
                <filter id="goldGlow" x="-40%" y="-40%" width="180%" height="180%">
                  <feGaussianBlur stdDeviation="14" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {!reduce &&
                PARTICLES.map((dx, i) => (
                  <circle
                    key={i}
                    className="funnel-particle"
                    cx={320 + dx}
                    cy={0}
                    r={4}
                    fill="#E9B83F"
                    opacity="0.85"
                    style={{ animationDelay: `${i * 0.75}s` }}
                  />
                ))}

              {STAGES.map((s, i) => {
                const y = 10 + i * 118;
                const isActive = i === active;
                const fill = isActive ? "#E9B83F" : "rgba(233,184,63,0.05)";
                return (
                  <g
                    key={s.name}
                    data-testid={`funnel-stage-${i}`}
                    onClick={() => select(i)}
                    onKeyDown={(e) => e.key === "Enter" && select(i)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Etapa ${i + 1}: ${s.name}`}
                    className="cursor-pointer outline-none"
                    style={{ transition: "opacity 0.4s" }}
                  >
                    <motion.path
                      d={`M ${320 - W[i] / 2} ${y} L ${320 + W[i] / 2} ${y} L ${320 + B[i] / 2} ${y + 104} L ${320 - B[i] / 2} ${y + 104} Z`}
                      animate={{
                        fill,
                        stroke: isActive ? "#E9B83F" : "rgba(233,184,63,0.3)",
                      }}
                      transition={{ duration: 0.5, ease: EASE }}
                      strokeWidth={isActive ? 2 : 1}
                      filter={isActive ? "url(#goldGlow)" : undefined}
                    />
                    <motion.text
                      x={320}
                      y={y + 48}
                      textAnchor="middle"
                      animate={{ fill: isActive ? "#090B0E" : "#F5F2EB" }}
                      className="font-display font-extrabold"
                      fontSize={22 - i * 1.5}
                      letterSpacing="0.18em"
                    >
                      {s.name}
                    </motion.text>
                    <motion.text
                      x={320}
                      y={y + 74}
                      textAnchor="middle"
                      animate={{ fill: isActive ? "rgba(9,11,14,0.75)" : "rgba(245,242,235,0.45)" }}
                      fontSize={11}
                      letterSpacing="0.08em"
                    >
                      {`0${i + 1}`}
                    </motion.text>
                  </g>
                );
              })}
            </svg>
          </Reveal>

          <div className="order-1 lg:order-2">
            <div className="flex gap-2 mb-8" data-testid="funnel-stage-tabs">
              {STAGES.map((s, i) => (
                <button
                  key={s.name}
                  data-testid={`funnel-tab-${i}`}
                  onClick={() => select(i)}
                  aria-label={`Etapa ${s.name}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === active ? "w-12 bg-gold" : "w-6 bg-bone/20 hover:bg-bone/40"
                  }`}
                />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={reduce ? false : { opacity: 0, x: 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? undefined : { opacity: 0, x: -24 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="bg-ink/60 backdrop-blur border border-gold/20 rounded-3xl p-8 lg:p-10"
                data-testid="funnel-detail-panel"
              >
                <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">
                  Etapa {String(active + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-3 font-display font-extrabold text-3xl lg:text-4xl text-bone tracking-tight">
                  {stage.name}
                </h3>
                <p className="mt-1 text-sm uppercase tracking-[0.18em] text-bone/50">{stage.tag}</p>
                <p className="mt-5 text-bone/70 leading-relaxed">{stage.desc}</p>

                <ul className="mt-6 space-y-3">
                  {stage.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-bone/80">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  {stage.metrics.map((m) => (
                    <div key={m.label} className="bg-graphite/60 border border-gold/15 rounded-2xl p-4">
                      <p className="font-display font-extrabold text-2xl text-gold">
                        <Counter
                          to={m.value}
                          format={(v) =>
                            `${m.prefix || ""}${v.toLocaleString("pt-BR", {
                              maximumFractionDigits: m.decimals || 0,
                              minimumFractionDigits: m.decimals || 0,
                            })}${m.suffix || ""}`
                          }
                        />
                      </p>
                      <p className="text-[11px] uppercase tracking-widest text-bone/50 mt-1">{m.label}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-[11px] text-bone/35">*Números ilustrativos baseados em cases reais do método.</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
